import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { config } from "@config";
import { skipWhile, take } from "rxjs/operators";
import { ServiceNames } from "@models/service_enums";

@Injectable({
	providedIn: "root"
})
export class InitService {
	private readyToInit: boolean = false; // Prevents services to set as initiated until the APP starts to init itself (AppComponent constructor)
	private _initiatedServices: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
	private whiteListRoute: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	public getInitiatedServices(): string[] {
		return this._initiatedServices.getValue();
	}

	public getInitiatedServicesAsObservable(): Observable<string[]> {
		return this._initiatedServices.asObservable();
	}

	public waitForInitServices(services: ServiceNames[]): Observable<string[]> {
		if (!services || services.length === 0) {
			return of(services);
		}

		return this.getInitiatedServicesAsObservable().pipe(
			skipWhile((initiated: string[]) => !services.every((service) => initiated.includes(service))),
			take(1)
		);
	}

	// Creates and returns an observable from the condition
	public getInitObservable(service: string): Observable<boolean> {
		return of(this.getInitiatedServices().includes(service));
	}

	// Checks if every service is initiated
	public didInit(services: string[]): boolean {
		return services.every((service) => this.getInitiatedServices().includes(service));
	}

	// Called when a service is ready for use and dependant pages/components can be loaded/used
	public init(service: string): void {
		if (service === "APP") {
			this.readyToInit = true;
			return;
		}
		if (!service || !this.readyToInit) {
			return;
		}

		const services = [...this._initiatedServices.getValue()];
		if (!services.includes(service)) {
			if (config.debug) {
				console.log(
					"%cINIT %c%s",
					"color:teal; font-weight:bold",
					"color:aquamarine; font-weight:bold",
					service
				);
			}
			services.push(service);
			this._initiatedServices.next(services);
		}
	}

	public getWhitelistRouteObservable(): Observable<boolean> {
		return this.whiteListRoute.asObservable();
	}

	public isServiceInitialised(s: string): boolean {
		return this._initiatedServices.getValue().includes(s);
	}

	public setWhitelistRoute(): void {
		this.whiteListRoute.next(true);
	}
}
